import React, { useState } from "react";
import Swal from "sweetalert2";

const API_URL = process.env.REACT_APP_API_URL;
const TODAY = new Date().toISOString().split("T")[0];

/* =========================================================
   INITIAL FORM  (exact API fields)
========================================================= */
const initialForm = {
  student_name: "",
  date_of_birth: "",
  gender: "",
  school_id: "",
  class_id: "",
  rte_status: "",
  caste_category: "",
  sub_caste: "",
  father_name: "",
  mother_name: "",
  parent_email: "",
  parent_phone: "",
  alternate_phone: "",
  parent_address: "",
  father_occupation: "",
  mother_occupation: "",
  father_annual_income: "",
  mother_annual_income: "",
  income_category: "",
  bpl_status: "No",
  message: "",
};

/* =========================================================
   FORM LAYOUT  (ek jagah se saare sections / fields)
========================================================= */
const opt = (...values) => values.map((v) => [v, v]);
const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => [
  String(i + 1),
  `Class ${i + 1}`,
]);

const SECTIONS = [
  {
    icon: "👦",
    title: "Student details",
    fields: [
      { name: "student_name", label: "Student full name", placeholder: "Enter full name", required: true },
      { name: "date_of_birth", label: "Date of birth", type: "date", required: true },
      { name: "gender", label: "Gender", kind: "select", placeholder: "Select gender", options: opt("Male", "Female", "Other"), required: true },
      { name: "school_id", label: "School", kind: "select", placeholder: "Select school", options: [["1", "School 1"], ["2", "School 2"], ["3", "School 3"]], required: true },
      { name: "class_id", label: "Class", kind: "select", placeholder: "Select class", options: CLASS_OPTIONS, required: true },
      { name: "rte_status", label: "RTE status", kind: "select", placeholder: "RTE / Non-RTE", options: opt("RTE", "Non-RTE"), required: true },
    ],
  },
  {
    icon: "🪪",
    title: "Caste / category",
    fields: [
      { name: "caste_category", label: "Category", kind: "select", placeholder: "Select category", options: opt("General", "OBC", "SC", "ST", "EWS"), required: true },
      { name: "sub_caste", label: "Sub caste", placeholder: "Optional" },
    ],
  },
  {
    icon: "👨‍👩‍👧",
    title: "Parent details",
    fields: [
      { name: "father_name", label: "Father's name", placeholder: "Enter father's name", required: true },
      { name: "mother_name", label: "Mother's name", placeholder: "Enter mother's name", required: true },
      { name: "parent_email", label: "Parent email", type: "email", placeholder: "name@example.com", required: true, full: true },
      { name: "parent_phone", label: "Parent phone", type: "tel", placeholder: "10-digit mobile number", maxLength: 10, required: true },
      { name: "alternate_phone", label: "Alternate phone", type: "tel", placeholder: "Optional", maxLength: 10 },
      { name: "parent_address", label: "Full address", kind: "textarea", rows: 3, maxLength: 250, placeholder: "House no., street, city, pincode", full: true },
    ],
  },
  {
    icon: "💰",
    title: "Income details",
    fields: [
      { name: "father_occupation", label: "Father's occupation", placeholder: "Optional" },
      { name: "mother_occupation", label: "Mother's occupation", placeholder: "Optional" },
      { name: "father_annual_income", label: "Father's annual income (₹)", type: "number", placeholder: "0" },
      { name: "mother_annual_income", label: "Mother's annual income (₹)", type: "number", placeholder: "0" },
      { name: "income_category", label: "Income category", kind: "select", placeholder: "Select income category", options: opt("Below 1 Lakh", "1-3 Lakh", "3-5 Lakh", "5-10 Lakh", "Above 10 Lakh"), required: true },
      { name: "bpl_status", label: "BPL status", kind: "select", options: opt("No", "Yes") },
    ],
  },
  {
    icon: "📝",
    title: "Additional information",
    fields: [
      { name: "message", label: "Message / query", kind: "textarea", rows: 4, maxLength: 500, placeholder: "Optional", full: true, counter: true },
    ],
  },
];

/* =========================================================
   VALIDATION
========================================================= */
const NAME_RE = /^[a-zA-Z\s.'-]+$/;
const PHONE_RE = /^[6-9][0-9]{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validateField = (name, value) => {
  const val = typeof value === "string" ? value.trim() : value;
  const max = (n, msg) => (val && val.length > n ? msg : "");

  switch (name) {
    case "student_name":
    case "father_name":
    case "mother_name":
      if (!val) return "This field is required.";
      if (val.length < 3) return "Must be at least 3 characters.";
      if (val.length > 60) return "Must be less than 60 characters.";
      if (!NAME_RE.test(val)) return "Only letters, spaces and . ' - allowed.";
      return "";

    case "date_of_birth": {
      if (!val) return "Date of birth is required.";
      const dob = new Date(val);
      if (isNaN(dob.getTime())) return "Invalid date.";
      const now = new Date();
      if (dob > now) return "Date cannot be in the future.";
      if ((now - dob) / (1000 * 60 * 60 * 24 * 365.25) > 30) return "Please check the date of birth.";
      return "";
    }

    case "gender": return val ? "" : "Please select a gender.";
    case "school_id": return val ? "" : "Please select a school.";
    case "class_id": return val ? "" : "Please select a class.";
    case "rte_status": return val ? "" : "Please select RTE status.";
    case "caste_category": return val ? "" : "Please select a category.";
    case "income_category": return val ? "" : "Please select an income category.";

    case "sub_caste": return max(60, "Sub caste must be less than 60 characters.");
    case "parent_address": return max(250, "Address cannot exceed 250 characters.");
    case "father_occupation":
    case "mother_occupation": return max(60, "Occupation cannot exceed 60 characters.");
    case "message": return max(500, "Message cannot exceed 500 characters.");

    case "parent_email":
      if (!val) return "Email is required.";
      return EMAIL_RE.test(val) ? "" : "Enter a valid email address.";

    case "parent_phone":
      if (!val) return "Phone number is required.";
      return PHONE_RE.test(val) ? "" : "Enter a valid 10-digit Indian mobile number.";

    case "alternate_phone":
      if (!val) return "";
      return PHONE_RE.test(val) ? "" : "Enter a valid 10-digit mobile number.";

    case "father_annual_income":
    case "mother_annual_income": {
      if (val === "") return "";
      const n = Number(val);
      if (isNaN(n) || n < 0) return "Enter a valid income amount.";
      if (n > 100000000) return "Amount seems too high.";
      return "";
    }

    default:
      return "";
  }
};

const validateAll = (data) =>
  Object.keys(data).reduce((acc, key) => {
    const error = validateField(key, data[key]);
    if (error) acc[key] = error;
    return acc;
  }, {});

/* =========================================================
   FIELD  (input / select / textarea + label + error)
========================================================= */
const FormField = ({ f, value, error, onChange, onBlur }) => {
  const id = `reg-${f.name}`;
  const common = {
    id,
    name: f.name,
    value: value ?? "",
    onChange,
    onBlur,
    className: `reg-control${error ? " has-error" : ""}`,
    "aria-invalid": !!error,
    "aria-describedby": error ? `${id}-error` : undefined,
  };

  let control;
  if (f.kind === "select") {
    control = (
      <select {...common}>
        {f.placeholder && <option value="">{f.placeholder}</option>}
        {f.options.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
    );
  } else if (f.kind === "textarea") {
    control = <textarea {...common} rows={f.rows || 3} maxLength={f.maxLength} placeholder={f.placeholder} />;
  } else {
    control = (
      <input
        {...common}
        type={f.type || "text"}
        maxLength={f.maxLength}
        placeholder={f.placeholder}
        max={f.type === "date" ? TODAY : undefined}
        min={f.type === "number" ? 0 : undefined}
        inputMode={f.type === "tel" ? "numeric" : undefined}
        autoComplete="off"
      />
    );
  }

  return (
    <div className={`reg-field${f.full ? " full" : ""}`}>
      <label htmlFor={id}>
        {f.label}
        {f.required && <b aria-hidden="true"> *</b>}
      </label>
      {control}
      {(error || f.counter) && (
        <div className="reg-meta">
          {error ? <span id={`${id}-error`} className="field-error">{error}</span> : <span />}
          {f.counter && <small>{(value || "").length}/{f.maxLength}</small>}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */
export default function StudentRegister() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState("");

  /* ---------- change ---------- */
  const change = (e) => {
    const { name, value } = e.target;
    const newValue =
      name === "parent_phone" || name === "alternate_phone"
        ? value.replace(/\D/g, "").slice(0, 10)
        : value;

    setForm((prev) => ({ ...prev, [name]: newValue }));

    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, newValue) }));
    }
    if (apiError) setApiError("");
  };

  /* ---------- blur ---------- */
  const blur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  /* ---------- submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    setTouched(Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    const validationErrors = validateAll(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstError}"]`);
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      Swal.fire({
        icon: "warning",
        title: "Please Check Your Form",
        text: "Please fill all required fields correctly.",
        confirmButtonText: "OK",
        confirmButtonColor: "#4f46e5",
      });
      return;
    }

    setLoading(true);

    const payload = {
      student_name: form.student_name.trim(),
      date_of_birth: form.date_of_birth,
      gender: form.gender,
      school_id: Number(form.school_id),
      class_id: Number(form.class_id),
      rte_status: form.rte_status,
      caste_category: form.caste_category,
      sub_caste: form.sub_caste.trim(),
      father_name: form.father_name.trim(),
      mother_name: form.mother_name.trim(),
      parent_email: form.parent_email.trim(),
      parent_phone: form.parent_phone.trim(),
      alternate_phone: form.alternate_phone.trim(),
      parent_address: form.parent_address.trim(),
      father_occupation: form.father_occupation.trim(),
      mother_occupation: form.mother_occupation.trim(),
      father_annual_income: form.father_annual_income ? Number(form.father_annual_income) : 0,
      mother_annual_income: form.mother_annual_income ? Number(form.mother_annual_income) : 0,
      income_category: form.income_category,
      bpl_status: form.bpl_status,
      message: form.message.trim(),
    };

    try {
      const response = await fetch(`${API_URL}pre-student-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        /* Laravel validation errors */
        if (data?.errors && typeof data.errors === "object") {
          const firstKey = Object.keys(data.errors)[0];
          throw new Error(
            data.errors[firstKey]?.[0] || "Please check the entered information."
          );
        }
        throw new Error(
          data?.message || data?.error || `Request failed with status ${response.status}`
        );
      }

      setForm(initialForm);
      setErrors({});
      setTouched({});
      setApiError("");
      setSubmitted(true);

      await Swal.fire({
        icon: "success",
        title: "Registration Submitted!",
        text: data?.message || "Student inquiry has been submitted successfully.",
        confirmButtonText: "OK",
        confirmButtonColor: "#4f46e5",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      const errorMessage = error?.message || "Something went wrong. Please try again.";
      setApiError(errorMessage);

      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: errorMessage,
        confirmButtonText: "Try Again",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------- render ---------- */
  return (
    <div className="reg-page">
      <div className="reg-card">
        {/* HEADER */}
        <header className="reg-head">
          <span className="reg-kicker">🎓 Student School Darpan</span>
          <h1 className="reg-title">Student Registration</h1>
          <p>Complete the pre-inquiry form for admission. Fields marked * are required.</p>
        </header>

        {/* FORM */}
        <form className="reg-body" onSubmit={handleSubmit} noValidate>
          {SECTIONS.map((section) => (
            <div className="reg-section" key={section.title}>
              <h3 className="reg-section-title">
                <span>{section.icon}</span>
                {section.title}
              </h3>

              <div className="reg-grid">
                {section.fields.map((f) => (
                  <FormField
                    key={f.name}
                    f={f}
                    value={form[f.name]}
                    error={touched[f.name] ? errors[f.name] : ""}
                    onChange={change}
                    onBlur={blur}
                  />
                ))}
              </div>
            </div>
          ))}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "⏳ Submitting..." : "🚀 Submit Student Inquiry"}
          </button>

          {submitted && (
            <div className="success-block" role="status">
              <span>✓</span>
              Student inquiry submitted successfully!
            </div>
          )}

          {apiError && (
            <div className="error-block" role="alert">
              <span>✕</span>
              {apiError}
            </div>
          )}
        </form>
      </div>

      <style>{`
        /* =====================================================
           STUDENT REGISTER — Premium · Clean · Scoped
        ====================================================== */

        .reg-page, .reg-page *, .reg-page *::before, .reg-page *::after { box-sizing: border-box; }

        .reg-page {
          --brand: #4f46e5;
          --ink: #0e1a3a;
          --muted: #5b6784;
          --line: #dde3f3;
          --danger: #dc2626;

          min-height: 100vh;
          padding: 40px 20px 56px;
          color: var(--ink);
          font-family: "Plus Jakarta Sans", Inter, "Segoe UI", Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          background:
            radial-gradient(circle at 50% -140px, rgba(79,70,229,.14), transparent 55%),
            linear-gradient(180deg, #f6f8fd, #eef2fb);
        }

        .reg-page h1, .reg-page h3, .reg-page p { margin: 0; }

        .reg-card {
          max-width: 1020px;
          margin: 0 auto;
          overflow: hidden;
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 26px;
          box-shadow: 0 2px 4px rgba(14,26,58,.05), 0 28px 70px rgba(14,26,58,.12);
        }

        /* ---------- HEADER ---------- */
        .reg-head {
          position: relative;
          padding: 40px 44px;
          overflow: hidden;
          color: #fff;
          background:
            radial-gradient(circle at 90% 0%, rgba(244,163,0,.22), transparent 38%),
            linear-gradient(135deg, #0e1a3a 0%, #312e81 60%, #4f46e5 100%);
        }
        .reg-kicker { font-size: 14px; font-weight: 700; color: #ffd58f; }
        .reg-title { margin: 8px 0 8px; font-size: clamp(26px, 4vw, 36px); font-weight: 800; letter-spacing: -.8px; }
        .reg-head p { max-width: 560px; font-size: 15px; line-height: 1.6; color: #cdd5ea; }

        /* ---------- BODY / SECTIONS ---------- */
        .reg-body { padding: 32px 40px 40px; }

        .reg-section {
          margin-bottom: 20px;
          padding: 26px;
          background: #fbfcff;
          border: 1px solid var(--line);
          border-radius: 20px;
        }

        .reg-section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px !important;
          font-size: 18px;
          font-weight: 700;
          color: var(--ink);
        }
        .reg-section-title span {
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          font-size: 18px;
          background: #eef0ff;
          border-radius: 11px;
        }

        .reg-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
        .reg-field { display: flex; flex-direction: column; min-width: 0; }
        .reg-field.full { grid-column: 1 / -1; }

        .reg-field label { margin-bottom: 7px; font-size: 13.5px; font-weight: 600; color: #26325a; }
        .reg-field label b { color: var(--danger); }

        /* ---------- CONTROLS ---------- */
        .reg-control {
          width: 100%;
          height: 48px;
          padding: 0 14px;
          font-family: inherit;
          font-size: 15px;
          color: var(--ink);
          background: #fff;
          border: 1.5px solid var(--line);
          border-radius: 12px;
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        select.reg-control { cursor: pointer; }
        textarea.reg-control { height: auto; min-height: 96px; padding: 13px 14px; line-height: 1.5; resize: vertical; }
        .reg-control::placeholder { color: #94a0bd; }
        .reg-control:hover { border-color: #b9c3e3; }
        .reg-control:focus { border-color: var(--brand); box-shadow: 0 0 0 4px rgba(79,70,229,.14); }
        .reg-control.has-error { border-color: var(--danger); box-shadow: 0 0 0 4px rgba(220,38,38,.12); }

        .reg-meta { display: flex; justify-content: space-between; gap: 10px; margin-top: 6px; }
        .field-error { font-size: 13px; font-weight: 500; color: var(--danger); }
        .reg-meta small { margin-left: auto; font-size: 12.5px; color: #7a86a1; }

        /* ---------- SUBMIT ---------- */
        .submit-btn {
          width: 100%;
          height: 54px;
          margin-top: 6px;
          font-family: inherit;
          font-size: 16px;
          font-weight: 800;
          color: #fff;
          cursor: pointer;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          border: 0;
          border-radius: 14px;
          box-shadow: 0 12px 28px rgba(79,70,229,.3);
          transition: transform .2s ease, box-shadow .2s ease, opacity .2s ease;
        }
        .submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 34px rgba(79,70,229,.42); }
        .submit-btn:active:not(:disabled) { transform: none; }
        .submit-btn:disabled { opacity: .7; cursor: not-allowed; }
        .submit-btn:focus-visible { outline: 3px solid #f4a300; outline-offset: 3px; }

        /* ---------- INLINE STATUS ---------- */
        .success-block, .error-block {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 16px;
          padding: 14px 16px;
          font-size: 14.5px;
          font-weight: 600;
          border-radius: 12px;
        }
        .success-block { color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; }
        .error-block { color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca; }
        .success-block span, .error-block span {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: grid;
          place-items: center;
          font-size: 13px;
          font-weight: 700;
          color: #fff;
          border-radius: 50%;
        }
        .success-block span { background: #10b981; }
        .error-block span { background: var(--danger); }

        /* ---------- RESPONSIVE ---------- */
        @media (max-width: 700px) {
          .reg-page { padding: 80px 0 0; }
          .reg-card { border-radius: 20px 20px 0 0; border-inline: 0; }
          .reg-head { padding: 26px 20px; }
          .reg-body { padding: 16px 14px 28px; }
          .reg-section { padding: 18px; border-radius: 16px; }
          .reg-grid { grid-template-columns: 1fr; gap: 16px; }
          .reg-control { height: 50px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .reg-page *, .reg-page *::before, .reg-page *::after { transition-duration: .01ms !important; }
        }
      `}</style>
    </div>
  );
}
